import { useCallback } from 'react';

import useEventListener from '../hooks/useLocalStorage/useEventListener';

export enum LocalStorageKeys {
  ONE_CLICK_LOGIN_EMAIL = 'one_click_login_email',
  INACTIVITY_LOGOUT = 'inactivity_logout',
  LAST_ACTIVITY = 'last_activity',
  DEV_TEST_EXPIRATION = 'dev_test_expiration',
  FINIX_IDEMPOTENCY_ID = 'finix_idempotency_id',
  COBUYER_BANNER_DISMISSED = 'cobuyer_banner_dismissed',
  SAVE_PASSWORD_PROMPT_DISMISSED_AT = 'save_password_prompt_dismissed_at',
}

export const triggerStorageEvent = (key: LocalStorageKeys) => {
  window.dispatchEvent(new StorageEvent('storage', { key }));
};

export const getLocalStorageItem = <T>(key: LocalStorageKeys): T | null => {
  const value = window.localStorage.getItem(key);

  try {
    return JSON.parse(value || '') as T;
  } catch {
    return null;
  }
};

export const setLocalStorageItem = <T>(key: LocalStorageKeys, value: T) => {
  window.localStorage.setItem(key, JSON.stringify(value));
  triggerStorageEvent(key);
};

export const getLocalStorageItemOrInitial = <T>(
  key: LocalStorageKeys,
  initial: T,
  typeCheck?: (value: T) => boolean,
): T => {
  const value = getLocalStorageItem<T>(key);

  if (value == null || (typeCheck && !typeCheck(value))) {
    setLocalStorageItem(key, initial);

    return initial;
  }
  return value;
};

export const useLocalStorageEvent = (
  key: LocalStorageKeys,
  callback: (event: StorageEvent) => void,
) => {
  const handleStorageChange = useCallback(
    (event: StorageEvent) => {
      if (!event?.key || event.key !== key) {
        return;
      }
      callback(event);
    },
    [key, callback],
  );

  useEventListener('storage', handleStorageChange);
};
